import React from 'react';
import { Box, Text, Button } from '@indeed/ifl-components';
import { Close } from '@indeed/ifl-icons';
import { jsProThemeButton } from '../jsProTheme/jsProTheme';
import useGettext from '../../../../util/GettextFactory';
import { useInitialState } from '../../../../lib/context/InitialStateContext';

export interface StandalonePageHeaderProps {
    /** Title displayed in the center */
    title: string;
    /** Called when close button is clicked */
    onClose: () => void;
}

/**
 * Reusable header component for standalone Auto Apply pages
 * Similar to AutoApplySetupNavigation but simplified for standalone pages
 *
 * Features:
 * - Centered title with close button
 * - Symmetric title padding reserves space for the end-aligned close button
 * - Follows AutoApplySetupNavigation styling patterns
 */
export const StandalonePageHeader: React.FC<StandalonePageHeaderProps> = ({ title, onClose }) => {
    const gt = useGettext();
    const { isJobSearchApp } = useInitialState();

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Preserve the Close button's height even when the title is one line.
                minBlockSize: (theme) => `calc(${theme.space[6]} + ${theme.space[3]})`,
                // Reserve the Close button's width on both sides to keep the title centered.
                paddingInline: (theme) => `calc(${theme.space[7]} + ${theme.space[2]})`,
            }}
        >
            <Text
                level={3}
                variant="contrast"
                sx={{
                    fontWeight: 700,
                    minInlineSize: 0,
                    overflowWrap: 'anywhere',
                    textAlign: 'center',
                }}
            >
                {title}
            </Text>

            <Button
                variant="tertiary"
                type="button"
                onClick={onClose}
                aria-label={gt.gettext('Close')}
                sx={{
                    ...jsProThemeButton.tertiary,
                    position: 'absolute',
                    insetInlineEnd: 0,
                    insetBlockStart: '50%',
                    transform: 'translateY(-50%)',
                    visibility: isJobSearchApp ? 'hidden' : undefined,
                }}
            >
                <Close size="md" aria-hidden />
            </Button>
        </Box>
    );
};
